import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { ITask } from '@modules/TasksModule/types';
import { AuthUser } from '@modules/AuthModule/lib/types';
import {
  ServiceSessionCreatedEvent,
  ServiceSessionEvent,
  ServiceSessionEventType,
  ServiceSessionOfferedEvent,
  ServiceSessionUserEvent,
  ServiceSessionWrapupEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';
import handleSessionDelivered from './events/handleSessionDelivered';
import handleSessionDeleted from './events/handleSessionDeleted';
import handleSessionCreated from './events/handleSessionCreated';
import handleSessionWrapupStarted from './events/handleSessionWrapupStarted';
import handleSessionWrapupCompleted from './events/handleSessionWrapupCompleted';
import handleSessionRescinded from './events/handleSessionRescinded';
import handleSessionOffered from './events/handleSessionOffered';
import handleSessionRejected from './events/handleSessionRejected';
import handleSessionHangup from './events/handleSessionHangup';
import {
  CallState,
  PhonePrepareOutgoingEvent,
} from '@modules/SoftPhoneModule/types';
import handleCallAnswered from './events/handleCallAnswered';
import handleCallMissed from './events/handleCallMissed';
import handleCallPlaced from './events/handleCallPlaced';
import handleCallEnded from './events/handleCallEnded';
import { inboxState, inboxActions } from './state';
import { InboxContext } from './types';
import handleTaskAccept from './events/handleTaskAccept';
import handleTaskReject from './events/handleTaskReject';
import handleTaskComplete from './events/handleTaskComplete';
import handleTaskPrepareOutbound from './events/handleTaskPrepare';
import handleTaskUnhold from './events/handleTaskUnhold';
export class InboxModule extends AppModule {
  private userId: string | null = null;
  override preload(): void {
    // auth handlers
    this.app.events.on('auth.signin', (user: AuthUser) => {
      this.userId = user.id;
    });
    this.app.events.on('auth.signout', () => {
      this.userId = null;
    });

    // task handlers
    this.app.events.on('task.action.accept', (task: ITask) =>
      handleTaskAccept(this.context, task),
    );
    this.app.events.on('task.action.reject', (task: ITask) =>
      handleTaskReject(this.context, task),
    );
    this.app.events.on('task.action.complete', (task: ITask) => {
      handleTaskComplete(this.context, task, false);
    });
    this.app.events.on('task.action.complete-classified', (task: ITask) =>
      handleTaskComplete(this.context, task, true),
    );
    this.app.events.on('task.action.unhold', (task: ITask) =>
      handleTaskUnhold(this.context, task),
    );

    // service session handlers
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.DELIVERED,
      (ev) => handleSessionDelivered(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.REJECTED,
      (ev) => handleSessionRejected(this.context, ev),
    );
    this.app.events.on<ServiceSessionOfferedEvent>(
      ServiceSessionEventType.OFFERED,
      (ev) => handleSessionOffered(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.RESCINDED,
      (ev) => handleSessionRescinded(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.HANGUP,
      (ev) => handleSessionHangup(this.context, ev),
    );
    this.app.events.on<ServiceSessionUserEvent>(
      ServiceSessionEventType.WRAPUP_COMPLETED,
      (ev) => handleSessionWrapupCompleted(this.context, ev),
    );
    this.app.events.on<ServiceSessionWrapupEvent>(
      ServiceSessionEventType.WRAPUP_STARTED,
      (ev) => handleSessionWrapupStarted(this.context, ev),
    );
    this.app.events.on<ServiceSessionCreatedEvent>(
      ServiceSessionEventType.CREATED,
      (ev) => handleSessionCreated(this.context, ev),
    );
    this.app.events.on<ServiceSessionEvent>(
      ServiceSessionEventType.DELETED,
      (ev) => handleSessionDeleted(this.context, ev),
    );

    // phone call handlers
    this.app.events.on('phone.call.answered', (ev: CallState) =>
      handleCallAnswered(this.context, ev),
    );
    this.app.events.on('phone.call.ended', (ev: CallState) =>
      handleCallEnded(this.context, ev),
    );
    this.app.events.on('phone.call.placed', (ev: CallState) =>
      handleCallPlaced(this.context, ev),
    );
    this.app.events.on('phone.call.missed', (ev: CallState) =>
      handleCallMissed(this.context, ev),
    );
    this.app.events.on<PhonePrepareOutgoingEvent>(
      'phone.prepare.outbound',
      (eventData) => handleTaskPrepareOutbound(this.context, eventData),
    );

    // user status handlers
    this.app.events.on('userStatus.updated.available', this.resetMissedCalls);
  }

  public setSelectedTaskId(id: string) {
    inboxActions.setSelectedTaskId(id);
  }

  private get context(): InboxContext {
    if (!this.userId) {
      throw new Error('User not signed in');
    }

    return {
      userId: this.userId,
      logger: this.app.logger,
      parent: this,
    };
  }

  public handleMissedExternalCall = () => {
    inboxState.missedCallsExternal += 1;
  };

  public handleMissedInternalCall = () => {
    inboxState.missedCallsInternal += 1;
  };

  public resetMissedCallsExternal = () => {
    inboxState.missedCallsExternal = 0;
  };

  public resetMissedCallsInternal = () => {
    inboxState.missedCallsInternal = 0;
  };

  public resetMissedCalls = () => {
    inboxState.missedCallsExternal = 0;
    inboxState.missedCallsInternal = 0;
  };

  public showClassificationModalForTaskId = (taskId: string) => {
    inboxState.showClassificationModalForTaskId = taskId;
  };
}

export default new InboxModule(appManager.events, appManager.logger('Inbox'));
